import Image from 'next/image';
import { useMemo } from 'react';

import clsx from 'clsx';
import ToggleText from '~/components/ui/ToggleText';
import { formatToHumanReadableDate } from '~/lib/util';
import { CommentV2Type } from '~/static/discussions/enums';
import DiscussionOptions from './DiscussionOptions';

export interface ICreatorReplyProps {
  avatarUrl: string;
  creatorUsername: string;
  replyDate: string;
  replyText: string;
  status: string;
  allowOptions: boolean;
  deleteReplyUrl: string;
  mutateComment?: () => void;
}

/**
 * Creator Reply Component
 *
 * @param avatarUrl - The avatar url of the creator
 * @param creatorUsername - The username of the creator
 * @param replyDate - The date the reply was posted
 * @param replyText - The text of the reply
 */

export function CreatorReply({
  avatarUrl,
  creatorUsername,
  replyDate,
  replyText,
  status,
  allowOptions,
  deleteReplyUrl,
  mutateComment,
}: Readonly<ICreatorReplyProps>): JSX.Element {
  const formattedDate = useMemo(
    () => formatToHumanReadableDate(replyDate, { month: 'short' }),
    [replyDate],
  );

  return (
    <div
      className={clsx([
        'flex flex-col items-start gap-2 self-stretch rounded-lg  p-4',
        status === 'approved' ? 'bg-sky-100' : 'bg-iceberg',
      ])}
      data-testid="discussion-creator-reply"
    >
      <div className="flex w-full items-center justify-between gap-4">
        <span
          className={clsx([
            'text-xs font-semibold',
            status === 'approved' ? 'text-bolt' : 'text-dollar',
          ])}
          data-testid="creator-reply-label"
        >
          {status === 'approved' ? 'Founder Team' : 'Sumo'}
        </span>
        {allowOptions && (
          <div className="shrink-0">
            <DiscussionOptions
              deleteUrl={deleteReplyUrl}
              discusstionType={CommentV2Type.Reply}
              mutateComment={mutateComment}
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Image
          className="h-6 w-6 shrink-0 rounded-full"
          src={avatarUrl}
          alt={creatorUsername}
          height={24}
          width={24}
          loader={({ src }) => `${src}?w=24&h=24`}
          data-testid="creator-avatar"
        />
        <p className="font-bold" data-testid="creator-username">
          {creatorUsername}
        </p>
        <span
          className="h-1.5 w-1.5 rounded-full bg-gravel"
          aria-hidden="true"
        />
        <span className="text-sm text-grace" data-testid="creator-posted-date">
          {formattedDate}
        </span>
      </div>
      <ToggleText text={replyText} />
    </div>
  );
}
